import { Button, Card, Checkbox, Col, Form, Input, InputNumber, message, Row, Select } from "antd";
import CustomDatePicker from "common/CustomDatePicker";
import { ProductionPlanningContext } from "context/ProductionPlanningContext";
import moment from "moment";
import { useContext, useMemo } from "react";
import { isArrayAndHasValue } from "utils/functions";

const ProductionPlanningForm = (props) => {
  // Props
  const { editMode } = props;

  // Context
  const {
    form,
    options,
    onGenerate,
    onInquiryAndStyleChange,
    planDataChangeOnLC,
    getInquiryList,
    getStyleList,
    getSubStyleList,
    onSelectBuyerInquiry,
    onSelectStyle,
    onSelectSubStyle,
    onChangeCreatedStatus,
    isGenerateBtnEnable,
    startDateOrLineChange,
    selectedPlanDetails,
    onPcdRequestChange,
    resetPcdRequests,
  } = useContext(ProductionPlanningContext);

  const line = Form.useWatch("line", form);
  const use_both_style_po_watch = Form.useWatch("use_both_style_po", form);
  const subcontract_status_watch = Form.useWatch("subcontract_status", form);
  const inquiry_id_watch = Form.useWatch("inquiry_id", form);
  const style_id_watch = Form.useWatch("style_id", form);
  const sub_style_id_watch = Form.useWatch("sub_style_id", form);
  const remaining_qty_watch = Form.useWatch("remaining_qty", form);

  const lineOptions = useMemo(() => {
    const list = (Array.isArray(options?.lines) ? options.lines : []).map((line) => ({
      value: Number(line.value),
      label: line.label,
    }));
    const lineId = selectedPlanDetails?.line_id;
    if (editMode && lineId != null) {
      const numericId = Number(lineId);
      const label = selectedPlanDetails?.line_name;
      const index = list.findIndex((line) => line.value === numericId);
      if (index === -1) {
        list.unshift({ value: numericId, label: label || `Line ${numericId}` });
      } else if (label) {
        list[index] = { ...list[index], label };
      }
    }
    return list;
  }, [options?.lines, editMode, selectedPlanDetails]);

  const poOptions = useMemo(() => {
    const list = isArrayAndHasValue(options?.po) ? [...options.po] : [];
    if (editMode && isArrayAndHasValue(selectedPlanDetails?.po_maps)) {
      selectedPlanDetails.po_maps.forEach((item) => {
        const poMapId = item?.po_map_id;
        if (poMapId && !list.some((po) => po?.value === poMapId)) {
          list.push({ value: poMapId, label: item?.original_po || item?.po?.original_po || `PO #${poMapId}` });
        }
      });
    }
    return list;
  }, [options?.po, editMode, selectedPlanDetails]);

  const onCheckLearningCurve = (event) => {
    // planDataChangeOnLC(event.target.checked);
  };

  const inquiryFieldCheck = (fieldName) => {
    if (line) {
      if (editMode) {
        if (fieldName) {
          return false;
        } else {
          return true;
        }
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  return (
    <Card bodyStyle={{ paddingBottom: 0 }}>
      <Form layout="vertical" form={form} onFinish={onGenerate}>
        <Row gutter={[16, 8]} align={"middle"}>
          <Col span={4}>
            <Form.Item name="subcontract_status" hidden />
            <Form.Item name="sub_contract_factory_id" hidden />
            <Form.Item name="update_id" hidden />
            <Form.Item name="created_with" hidden />
            <Form.Item
              label="Line"
              name="line"
              rules={[
                {
                  required: true,
                  message: "Line is required",
                },
              ]}
            >
              <Select
                showSearch
                placeholder="Select Line"
                options={lineOptions}
                size="small"
                optionFilterProp="label"
                disabled={editMode}
                onSelect={(value) => {
                  const start_date = form.getFieldValue("start_date");
                  if (start_date) {
                    startDateOrLineChange({ line_id: value, start_date });
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="inquiry_id_when_not_selected" hidden />
            <Form.Item label="Inquiry" name="inquiry_id">
              <Select
                showSearch
                placeholder="Select Inquiry"
                optionFilterProp="children"
                filterOption={false}
                size="small"
                onSearch={(value) =>
                  getInquiryList({
                    inquiry_no: value,
                  })
                }
                allowClear
                onClear={() => {
                  getInquiryList();
                  resetPcdRequests();
                  form.setFieldsValue({
                    extra_cutting_percentage: 0,
                  });
                }}
                onSelect={(value) => {
                  onSelectBuyerInquiry(value);
                  onInquiryAndStyleChange({ inquiry_id_prop: value });
                  onChangeCreatedStatus("with_inquiry");
                  form.setFieldsValue({ sales_contract_po_id: [] });
                }}
                disabled={inquiryFieldCheck(inquiry_id_watch) || editMode}
              >
                {isArrayAndHasValue(options?.inquiryList) &&
                  options?.inquiryList?.map((item) => {
                    return (
                      <Select.Option value={item.id} key={item.id}>
                        {item?.inquiry_no || ""}
                      </Select.Option>
                    );
                  })}
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Style" name="style_id">
              <Select
                showSearch
                placeholder="Select Style"
                optionFilterProp="children"
                size="small"
                onSearch={(value) => getStyleList({ style_no: value, active_sales_contract: true })}
                allowClear
                onClear={() => {
                  getStyleList();
                  form.setFieldsValue({ sub_style_id: null });
                  getSubStyleList();
                }}
                onSelect={(value) => {
                  onSelectStyle(value);
                  onInquiryAndStyleChange({
                    style_id_prop: value,
                    options: options,
                  });
                  onChangeCreatedStatus("with_style_substyle");
                  form.setFieldsValue({ sales_contract_po_id: [] });
                }}
                disabled={inquiryFieldCheck(style_id_watch) || editMode}
              >
                {isArrayAndHasValue(options?.styleList) &&
                  options?.styleList?.map((item) => {
                    return (
                      <Select.Option value={item.id} key={item.id}>
                        {item?.style_no || ""}
                      </Select.Option>
                    );
                  })}
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Sub Style" name="sub_style_id">
              <Select
                showSearch
                placeholder="Select Sub Style"
                optionFilterProp="children"
                size="small"
                onSearch={(value) => getSubStyleList({ sub_style_no: value })}
                allowClear
                onClear={getSubStyleList}
                onSelect={(value) => {
                  onSelectSubStyle(value);
                  onInquiryAndStyleChange({
                    sub_style_id_prop: value,
                    options: options,
                  });
                  onChangeCreatedStatus("with_style_substyle");
                }}
                disabled={inquiryFieldCheck(sub_style_id_watch) || editMode}
              >
                {isArrayAndHasValue(options?.subStyleList) &&
                  options?.subStyleList?.map((item) => {
                    return (
                      <Select.Option value={item.id} key={item.id}>
                        {item?.sub_style_no}
                      </Select.Option>
                    );
                  })}
              </Select>
            </Form.Item>
          </Col>
          {/* PCD after Sub Style */}
          <Col span={4}>
            {isArrayAndHasValue(options?.pcdRequests) ? (
              <>
                <Form.Item name="pcd" hidden />
                <Form.Item label="PCD" name="pcd_request_key">
                  <Select
                    placeholder="Select PCD"
                    size="small"
                    options={options.pcdRequests}
                    onChange={onPcdRequestChange}
                    disabled={editMode}
                  />
                </Form.Item>
              </>
            ) : (
              <Form.Item label="PCD" name="pcd">
                <Input placeholder="PCD" disabled size="small" />
              </Form.Item>
            )}
          </Col>
          <Col span={subcontract_status_watch ? 4 : 0}>
            <Form.Item label="Factory Name" name="factory_name">
              <Input placeholder="Factory Name" size="small" disabled />
            </Form.Item>
          </Col>
          <Col span={subcontract_status_watch ? 0 : 4}>
            <Form.Item label="Buyer" name={["buyer", "id"]} hidden>
              <Input size="small" />
            </Form.Item>
            <Form.Item label="Buyer" name={["buyer", "name"]} hidden={subcontract_status_watch ? true : false}>
              <Input disabled size="small" />
            </Form.Item>
          </Col>
          <Col span={subcontract_status_watch ? 0 : 4}>
            <Form.Item label="Buying Office" name={["buying_office", "id"]} hidden>
              <Input size="small" />
            </Form.Item>
            <Form.Item
              label="Buying Office"
              name={["buying_office", "name"]}
              hidden={subcontract_status_watch ? true : false}
            >
              <Input disabled size="small" />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Sales Contract No" name={["sales_contract", "id"]} hidden>
              <Input showSearch placeholder="Sales Contract No" disabled size="small" />
            </Form.Item>
            <Form.Item label="Sales Contract No" name={["sales_contract", "sales_contract_no"]}>
              <Input showSearch placeholder="Sales Contract No" disabled size="small" />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              label="PO"
              name="sales_contract_po_id"
              rules={[
                {
                  required: use_both_style_po_watch === "Yes" ? true : false,
                  message: "PO is required",
                },
              ]}
            >
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                maxTagCount="responsive"
                placeholder="Select PO"
                options={poOptions}
                onChange={(values) => {
                  const selectedPoMapIds = values || [];
                  const sales_order_quantity = (options?.po || [])
                    .filter((item) => selectedPoMapIds.includes(item?.id))
                    .reduce((sum, item) => sum + (Number(item?.sales_order_quantity) || 0), 0);
                  form.setFieldsValue({
                    origin_order_qty: sales_order_quantity,
                  });
                  onInquiryAndStyleChange({
                    po_map_ids: selectedPoMapIds,
                    style_id_prop: style_id_watch,
                  });
                }}
                size="small"
                disabled={editMode}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="origin_order_qty" hidden>
              <InputNumber />
            </Form.Item>
            <Form.Item label="Origin Order Qty" name="origin_order_qty_display">
              <InputNumber
                placeholder="Origin Order Qty"
                disabled
                style={{ width: "100%" }}
                size="small"
                precision={0}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Cutting %" name="extra_cutting_percentage">
              <InputNumber placeholder="Cutting %" disabled style={{ width: "100%" }} size="small" precision={2} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="With (%) Qty" name="order_qty">
              <InputNumber placeholder="With (%) Qty" disabled style={{ width: "100%" }} size="small" precision={0} />
            </Form.Item>
          </Col>
          {/* Remaining Qty, Planned Qty, Actual Produced Qty after Order Qty */}
          <Col span={4}>
            <Form.Item name="existing_plan_sum" hidden />
            <Form.Item label="Remaining Qty" name="remaining_qty">
              <InputNumber style={{ width: "100%" }} placeholder="Remaining Qty" disabled size="small" precision={0} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Planned Qty" name="planned_qty">
              <InputNumber style={{ width: "100%" }} precision={0} placeholder="Planned Qty" disabled size="small" />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Actual Produced Qty" name="actual_qty">
              <Input placeholder="Actual Produced Qty" disabled size="small" />
            </Form.Item>
          </Col>
          {/* Shipment Date followed by Start Date */}
          <Col span={4}>
            <Form.Item
              label="Shipment Date"
              name="shipment_date"
              rules={[
                {
                  required: true,
                  message: "Shipment Date is required",
                },
              ]}
            >
              <CustomDatePicker
                disabled={editMode}
                disabledDate={(current) => {
                  return current && current < moment().startOf("day");
                }}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              label="Start Date"
              name="start_date"
              rules={[
                {
                  required: true,
                  message: "Start Date is required",
                },
              ]}
            >
              <CustomDatePicker
                disabled={editMode}
                disabledDate={(current) => {
                  return current && current < moment().startOf("day");
                }}
                onChange={(date, dateString) => {
                  const line = form.getFieldValue("line");
                  if (line && dateString) {
                    startDateOrLineChange({
                      line_id: line,
                      start_date: dateString,
                    });
                  } else {
                    message.error("Please select line and start date before generating plan.");
                    form.setFieldsValue({ start_date: null });
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="SAM" name="smv">
              <Input placeholder="SAM" disabled size="small" />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="MP" name="no_of_workers">
              <InputNumber placeholder="MP" disabled size="small" style={{ width: "100%" }} precision={2} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Hourly Production" name="per_hour_target">
              <Input placeholder="Hourly Production" disabled size="small" />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Order Status" name="order_status_name">
              <Input placeholder="Order Status" disabled size="small" />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Planned MH" name="planned_mh">
              <Input placeholder="Planned MH" disabled size="small" />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Actual MH" name="actual_mh">
              <Input placeholder="Actual MH" disabled size="small" />
            </Form.Item>
          </Col>
          <Col span={0}>
            <Form.Item name="ob_id" hidden />
            <Form.Item name="learning_curve_id" hidden />
          </Col>
          <Col span={0}>
            <Form.Item name="use_both_style_po" hidden />
          </Col>
          <Col span={4}>
            <Form.Item
              label="Apply Learning Curve"
              name="is_apply_learning_curve"
              valuePropName="checked"
              initialValue={true}
            >
              <Checkbox size="small" onChange={onCheckLearningCurve} disabled={editMode} />
            </Form.Item>
          </Col>
          <Col
            flex="auto"
            style={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-end", paddingBottom: 24 }}
          >
            <Button
              type="primary"
              htmlType="submit"
              disabled={remaining_qty_watch <= 0 || isGenerateBtnEnable === 0 || editMode}
              style={{ minWidth: 100 }}
            >
              Generate
            </Button>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default ProductionPlanningForm;
